"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { m, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
export interface TrendingUpIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface TrendingUpIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}
const SVG_VARIANTS: Variants = {
  normal: {
    translateX: 0,
    translateY: 0
  },
  animate: {
    translateX: [0, 2, 0],
    translateY: [0, -2, 0],
    transition: {
      duration: 0.5
    }
  }
};
const PATH_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    pathLength: 1,
    transition: {
      duration: 0.4,
      opacity: {
        duration: 0.1
      }
    }
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    pathOffset: [1, 0],
    transition: {
      duration: 0.4,
      opacity: {
        duration: 0.1
      }
    }
  }
};
const ARROW_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    pathLength: 1,
    transition: {
      delay: 0.3,
      duration: 0.3,
      opacity: {
        duration: 0.1,
        delay: 0.3
      }
    }
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    pathOffset: [0.5, 0],
    transition: {
      delay: 0.3,
      duration: 0.3,
      opacity: {
        duration: 0.1,
        delay: 0.3
      }
    }
  }
};
const TrendingUpIcon = forwardRef<TrendingUpIconHandle, TrendingUpIconProps>((t0, ref) => {
  const $ = _c(33);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? 20 : t1;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t2;
  if ($[6] !== controls) {
    t2 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[6] = controls;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[8] !== controls || $[9] !== onMouseEnter) {
    t3 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[8] = controls;
    $[9] = onMouseEnter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[11] !== controls || $[12] !== onMouseLeave) {
    t4 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[11] = controls;
    $[12] = onMouseLeave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("inline-flex items-center justify-center", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== size) {
    t6 = {
      width: size,
      height: size
    };
    $[16] = size;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  let t8;
  if ($[18] !== controls) {
    t7 = <m.polyline points="22 7 13.5 15.5 8.5 10.5 2 17" variants={PATH_VARIANTS} initial="normal" animate={controls} />;
    t8 = <m.polyline points="16 7 22 7 22 13" variants={ARROW_VARIANTS} initial="normal" animate={controls} />;
    $[18] = controls;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  let t9;
  if ($[21] !== controls || $[22] !== size || $[23] !== t7 || $[24] !== t8) {
    t9 = <m.svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={SVG_VARIANTS} initial="normal" animate={controls}>{t7}{t8}</m.svg>;
    $[21] = controls;
    $[22] = size;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== handleMouseEnter || $[27] !== handleMouseLeave || $[28] !== props || $[29] !== t5 || $[30] !== t6 || $[31] !== t9) {
    t10 = <div className={t5} style={t6} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t9}</div>;
    $[26] = handleMouseEnter;
    $[27] = handleMouseLeave;
    $[28] = props;
    $[29] = t5;
    $[30] = t6;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
});
TrendingUpIcon.displayName = "TrendingUpIcon";
export { TrendingUpIcon };